// « Entretien » (offre Diagnostic) : le DSI répond, à son rythme, aux questions qu'Ekera pose sur ce que les pièces
// n'établissent pas et qui changent la carte, les pertes potentielles estimées ou l'indicateur NIS2. Une réponse est
// enregistrée tout de suite et citée comme source « entretien » ; « Je ne sais pas » laisse un point à confirmer ; une
// question peut être déléguée à un collègue, qui devra se connecter pour répondre (le lien seul n'ouvre rien).
// Aucun texte n'est rédigé ici : questions et raisons viennent de l'API, montants du moteur.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { expliquerErreur } from '../api/client';
import type { EtatEntretien, QuestionEntretien } from '../api/types';
import { Attendre, Etiquette } from '../composants/communs';
import { useEkera } from '../contexte';
import { dateFr, eurosCourt, nomDePersonne } from '../domaine/format';
import { lienVers } from '../routeur';

export function EcranEntretien({ delegation }: { delegation?: string | null }) {
  if (delegation) return <QuestionDelegueeVue did={delegation} />;
  return <Entretien />;
}

function Entretien() {
  const { client, cid, ecritureAutorisee } = useEkera();
  const qc = useQueryClient();
  const etat = useQuery({ queryKey: ['carte', cid, 'entretien'], queryFn: () => client.entretien(cid) });
  const preparer = useMutation({
    mutationFn: () => client.preparerEntretien(cid),
    onSuccess: (e) => qc.setQueryData(['carte', cid, 'entretien'], e),
  });
  return (
    <Attendre etats={[etat]} quoi="de l’entretien">
      {() => {
        const e = etat.data;
        if (!e) return null;
        return (
          <div className="pile">
            <section className="panneau" aria-labelledby="t-entretien-intro">
              <h2 id="t-entretien-intro">Les questions que vos documents ne règlent pas</h2>
              <p>
                Ekera ne vous interroge que sur ce que les pièces n’établissent pas et qui change la carte, les pertes potentielles
                estimées (conditionnelles aux hypothèses retenues) ou l’indicateur de progression NIS2. Répondez à votre rythme :
                chaque réponse est enregistrée aussitôt et citée dans la carte comme source « entretien ».
              </p>
              <p className="note" data-testid="mode-entretien">
                {e.mode === 'regles'
                  ? 'Vos réponses sont lues sans modèle de langage : choisissez une option, donnez un montant, ou répondez simplement par oui ou non quand c’est possible.'
                  : 'Vos réponses en phrases sont lues par un modèle de langage hébergé en France, qui ne peut changer que l’élément visé par la question.'}
              </p>
              {e.preparee ? (
                <p>
                  {e.a_poser} question{e.a_poser > 1 ? 's' : ''} à poser · {e.repondues} répondue{e.repondues > 1 ? 's' : ''} ·{' '}
                  {e.inconnues} point{e.inconnues > 1 ? 's' : ''} à confirmer avant de prendre une décision.
                </p>
              ) : null}
              {ecritureAutorisee && (
                <div className="ligne">
                  <button type="button" className="btn" disabled={preparer.isPending} onClick={() => preparer.mutate()}>
                    {e.preparee ? 'Mettre à jour les questions (après un nouveau dépôt)' : 'Préparer l’entretien'}
                  </button>
                </div>
              )}
              {preparer.error && (
                <p role="alert" className="encadre attention">
                  {expliquerErreur(preparer.error)}
                </p>
              )}
            </section>
            {e.preparee && <Questions e={e} />}
          </div>
        );
      }}
    </Attendre>
  );
}

function Questions({ e }: { e: EtatEntretien }) {
  const a = e.questions.filter((q) => q.etat === 'a_poser' || q.etat === 'deleguee');
  const faites = e.questions.filter((q) => q.etat === 'repondue' || q.etat === 'inconnue');
  const ecartees = e.questions.filter((q) => q.etat === 'ecartee');
  return (
    <>
      <section className="panneau" aria-labelledby="t-a-poser" data-testid="questions-a-poser">
        <h2 id="t-a-poser">À répondre ({a.length})</h2>
        {a.length === 0 ? (
          <p>
            Plus aucune question n’attend de réponse. <a href={lienVers('carte')}>Voir la carte</a>.
          </p>
        ) : (
          <ol className="pile" style={{ paddingLeft: 0, listStyle: 'none' }}>
            {a.map((q) => (
              <li key={q.id}>
                <CarteQuestion q={q} />
              </li>
            ))}
          </ol>
        )}
      </section>
      {faites.length > 0 && (
        <section className="panneau" aria-labelledby="t-faites" data-testid="questions-repondues">
          <h2 id="t-faites">Réponses enregistrées ({faites.length})</h2>
          <ul className="pile" style={{ paddingLeft: 0, listStyle: 'none' }}>
            {faites.map((q) => (
              <li key={q.id} className="encadre">
                <p>
                  <b>{q.texte}</b>
                </p>
                <p>
                  {q.etat === 'inconnue' ? (
                    <Etiquette ton="attention">Point à confirmer</Etiquette>
                  ) : (
                    <Etiquette ton="ok">Répondue</Etiquette>
                  )}{' '}
                  « {q.reponse} »
                  <span className="note">
                    {' '}
                    — {nomDePersonne(q.repondu_par) ?? 'une personne'}
                    {nomDePersonne(q.a_la_demande_de) ? `, à la demande de ${nomDePersonne(q.a_la_demande_de)}` : ''}, le {dateFr(q.repondu_le)}
                  </span>
                </p>
                {(q.changements ?? []).length > 0 ? (
                  <p className="note">
                    {q.changements?.length} changement{(q.changements?.length ?? 0) > 1 ? 's' : ''} dans la carte, cité{(q.changements?.length ?? 0) > 1 ? 's' : ''}{' '}
                    « entretien:{q.id} ».
                  </p>
                ) : (
                  q.resume && <p className="note">{q.resume}</p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      {ecartees.length > 0 && (
        <details className="panneau">
          <summary>Questions écartées ({ecartees.length}) : elles ne changeraient pas le calcul</summary>
          <ul className="liste-points">
            {ecartees.map((q) => (
              <li key={q.id}>
                {q.texte} <span className="note">— {q.raison_ecartee}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}

export function effetLisible(q: QuestionEntretien): string | null {
  const ef = q.effet;
  if (!ef) return null;
  if (ef.euros !== null && ef.euros > 0.5)
    return `Selon votre réponse, les pertes potentielles estimées peuvent varier de ${eurosCourt(ef.euros)} par an (calcul du moteur).`;
  if (ef.rayon !== null && ef.rayon > 0) return 'Votre réponse change l’étendue de ce qu’une attaque arrêterait.';
  if (ef.nis2 > 0) return 'Votre réponse change l’indicateur de progression NIS2 sur les mesures évaluées.';
  if (ef.rayon === null || ef.euros === null) return 'Effet non calculable avec les informations disponibles.';
  return 'Votre réponse règle un point à confirmer entre vos pièces.';
}

function CarteQuestion({ q }: { q: QuestionEntretien }) {
  const { client, cid, ecritureAutorisee } = useEkera();
  const qc = useQueryClient();
  const [texte, setTexte] = useState('');
  const [dest, setDest] = useState('');
  const [lien, setLien] = useState<string | null>(null);
  const id = useId();
  const relire = async () => {
    // Le préfixe inclut scénarios et constats, même si leurs écrans sont absents pendant l’entretien.
    await qc.invalidateQueries({ queryKey: ['carte', cid], refetchType: 'all' });
    await qc.invalidateQueries({ queryKey: ['cartes'] });
  };
  const repondre = useMutation({
    mutationFn: (r: { reponse?: string; inconnu?: boolean }) => client.repondreEntretien(cid, q.id, r),
    onSuccess: async (r) => {
      qc.setQueryData(['carte', cid, 'entretien'], r.entretien);
      await relire();
    },
  });
  const deleguer = useMutation({
    mutationFn: () => client.delegerQuestion(cid, q.id, dest.trim()),
    onSuccess: async (d) => {
      setLien(`${window.location.origin}${window.location.pathname}${d.lien}`);
      await qc.invalidateQueries({ queryKey: ['carte', cid, 'entretien'] });
    },
  });
  const effet = effetLisible(q);
  return (
    <article className="encadre" aria-labelledby={`${id}-t`} data-testid={`question-${q.id}`}>
      <h3 id={`${id}-t`}>
        {q.rang ? `${q.rang}. ` : ''}
        {q.texte}
      </h3>
      <p className="note">Pourquoi cette question : {q.pourquoi}</p>
      {effet && <p className="note">{effet}</p>}
      {q.etat === 'deleguee' && q.delegation && (
        <p>
          <Etiquette ton="info">Déléguée</Etiquette>{' '}
          <span className="note">
            {q.delegation.destinataire ? `à ${q.delegation.destinataire}, ` : ''}jusqu’au {dateFr(q.delegation.expire_le)}. Vous pouvez aussi
            répondre vous-même.
          </span>
        </p>
      )}
      {ecritureAutorisee && (
        <div className="pile">
          {(q.options ?? []).length > 0 && (
            <div className="ligne" role="group" aria-label="Réponses proposées">
              {(q.options ?? []).map((o) => (
                <button key={o} type="button" className="btn" disabled={repondre.isPending} onClick={() => repondre.mutate({ reponse: o })}>
                  {o}
                </button>
              ))}
            </div>
          )}
          <div className="champ">
            <label htmlFor={`${id}-r`}>Votre réponse</label>
            <textarea id={`${id}-r`} rows={2} maxLength={2000} value={texte} onChange={(e) => setTexte(e.target.value)} />
          </div>
          <div className="ligne">
            <button
              type="button"
              className="btn principal"
              disabled={!texte.trim() || repondre.isPending}
              onClick={() => repondre.mutate({ reponse: texte })}
            >
              Enregistrer la réponse
            </button>
            <button type="button" className="btn" disabled={repondre.isPending} onClick={() => repondre.mutate({ inconnu: true })}>
              Je ne sais pas
            </button>
          </div>
          {repondre.error && (
            <p role="alert" className="encadre attention">
              {expliquerErreur(repondre.error)}
            </p>
          )}
          <details>
            <summary>Déléguer à un collègue</summary>
            <div className="pile">
              <div className="champ">
                <label htmlFor={`${id}-d`}>Courriel du collègue (obligatoire)</label>
                <input
                  id={`${id}-d`}
                  type="email"
                  required
                  aria-required="true"
                  maxLength={254}
                  value={dest}
                  autoComplete="off"
                  onChange={(e) => setDest(e.target.value)}
                />
              </div>
              <p className="note">
                Le lien ne suffit pas : seule la personne connectée à Ekera avec le compte de l’entreprise dont l’adresse vérifiée est
                celle-ci pourra lire cette question et y répondre, une fois, sous quatorze jours.
              </p>
              <div className="ligne">
                <button type="button" className="btn" disabled={!dest.trim() || deleguer.isPending} onClick={() => deleguer.mutate()}>
                  Créer le lien de délégation
                </button>
              </div>
              {lien && (
                <p role="status">
                  Lien à transmettre : <span className="num">{lien}</span>
                </p>
              )}
              {deleguer.error && (
                <p role="alert" className="encadre attention">
                  {expliquerErreur(deleguer.error)}
                </p>
              )}
            </div>
          </details>
        </div>
      )}
    </article>
  );
}

function QuestionDelegueeVue({ did }: { did: string }) {
  const { client, cid } = useEkera();
  const [texte, setTexte] = useState('');
  const id = useId();
  const q = useQuery({ queryKey: ['delegation', cid, did], queryFn: () => client.questionDeleguee(cid, did), retry: false });
  const repondre = useMutation({ mutationFn: (r: { reponse?: string; inconnu?: boolean }) => client.repondreDelegation(cid, did, r) });
  return (
    <Attendre etats={[q]} quoi="de la question déléguée">
      {() =>
        q.data ? (
          <section className="panneau" aria-labelledby={`${id}-t`} data-testid="question-deleguee">
            <h2 id={`${id}-t`}>Une question vous est déléguée</h2>
            <p className="note">
              {nomDePersonne(q.data.demandee_par) ? `${nomDePersonne(q.data.demandee_par)} vous` : 'On vous'} demande votre aide pour le diagnostic de{' '}
              {q.data.entreprise}. Votre réponse sera citée dans la carte comme source « entretien », à votre nom.
            </p>
            <h3>{q.data.texte}</h3>
            <p className="note">Pourquoi cette question : {q.data.pourquoi}</p>
            {repondre.data ? (
              <p role="status">
                <Etiquette ton="ok">Merci</Etiquette> Votre réponse est enregistrée.
              </p>
            ) : q.data.etat !== 'ouverte' ? (
              <p role="alert">Cette question a déjà reçu une réponse, ou le lien a expiré.</p>
            ) : (
              <div className="pile">
                {(q.data.options ?? []).length > 0 && (
                  <div className="ligne" role="group" aria-label="Réponses proposées">
                    {(q.data.options ?? []).map((o) => (
                      <button key={o} type="button" className="btn" onClick={() => repondre.mutate({ reponse: o })}>
                        {o}
                      </button>
                    ))}
                  </div>
                )}
                <div className="champ">
                  <label htmlFor={`${id}-r`}>Votre réponse</label>
                  <textarea id={`${id}-r`} rows={2} maxLength={2000} value={texte} onChange={(e) => setTexte(e.target.value)} />
                </div>
                <div className="ligne">
                  <button type="button" className="btn principal" disabled={!texte.trim() || repondre.isPending} onClick={() => repondre.mutate({ reponse: texte })}>
                    Enregistrer la réponse
                  </button>
                  <button type="button" className="btn" disabled={repondre.isPending} onClick={() => repondre.mutate({ inconnu: true })}>
                    Je ne sais pas
                  </button>
                </div>
                {repondre.error && (
                  <p role="alert" className="encadre attention">
                    {expliquerErreur(repondre.error)}
                  </p>
                )}
              </div>
            )}
          </section>
        ) : null
      }
    </Attendre>
  );
}
